// ~50 LOC vendored polling hook — replaces TanStack Query (which was hit
// by Mini Shai-Hulud CVE-2026-45321 / CVSS 9.6 on 2026-05-11). Owned by
// us, no transitive deps, audited line-by-line.
//
// Behaviour:
//   * fetches on mount via the provided async fn
//   * polls every `intervalMs` (default 5 min)
//   * pauses when the document is hidden (visibilitychange)
//   * cancels in-flight requests on unmount / refetch via AbortController
//   * exposes `isStale` once `staleAfterMs` has passed since the last fetch

import { useEffect, useRef, useState } from "preact/hooks";

export interface PollResult<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  lastFetched: Date | undefined;
  refetch: () => void;
}

export interface PollOptions {
  intervalMs?: number;
  staleAfterMs?: number;
  enabled?: boolean;
}

export function usePoll<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: PollOptions = {},
): PollResult<T> {
  const intervalMs = options.intervalMs ?? 5 * 60_000;
  const enabled = options.enabled ?? true;

  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [loading, setLoading] = useState<boolean>(enabled);
  const [lastFetched, setLastFetched] = useState<Date | undefined>();
  const abortRef = useRef<AbortController | null>(null);
  const tickRef = useRef<number | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const tick = () => {
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    setLoading(true);
    fetcherRef.current(ctl.signal)
      .then((result) => {
        if (ctl.signal.aborted) return;
        setData(result);
        setError(undefined);
        setLastFetched(new Date());
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
  };

  // Re-fetch when deps change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return;
    tick();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  // Polling — pause when tab hidden, restart on visibility.
  useEffect(() => {
    if (!enabled) return;
    const schedule = () => {
      stopTick();
      if (document.hidden) return;
      tickRef.current = window.setInterval(tick, intervalMs);
    };
    const stopTick = () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
    const onVis = () => {
      if (document.hidden) {
        stopTick();
      } else {
        // Fetch immediately when becoming visible again, then resume polling.
        tick();
        schedule();
      }
    };
    schedule();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopTick();
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, ...deps]);

  return {
    data,
    error,
    loading,
    lastFetched,
    refetch: tick,
  };
}
